//

import React, { useState, useEffect } from "react";
import "./TutorCard.css";
import { Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import { useUserLocation } from "../../providers/LocationProvider";
import { useQuery } from "@tanstack/react-query";
import api from "../../utils/api";
import { getImageSrc } from "../../utils/getImageSrc";
import { formatRate, formatJoinedDate } from "../../utils/formatters";
import BadgeList from "../badgeList/BadgeList";
import { useCompare } from "../../providers/CompareProvider";

// Fetcher outside component — stable reference, React Query deduplicates
// calls automatically so if 8 cards share the same tutor id it only fires once
const fetchRating = (id) =>
  api.get(`/reviews?reviewed=${id}`).then((r) => r.data);

// imagePriority: "eager" for above-the-fold cards, "lazy" for the rest
// Passed in from FeaturedProvidersSection based on card index
const TutorCard = ({
  tutor: initialTutor,
  imagePriority = "lazy",
  showJoinedDate = false,
  enableCompare = false,
}) => {
  const { location: userLocation } = useUserLocation();
  // 1. Add this state near the top of the component
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const { isSelected, toggle, selected, maxCompare } = useCompare();

  // ── Rating via React Query (cached per tutor id) ────────────────────────
  const { data: ratingData } = useQuery({
    queryKey: ["tutor-rating", initialTutor._id],
    queryFn: () => fetchRating(initialTutor._id),
    enabled: !!initialTutor._id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Merge fetched rating into tutor data — fallback to whatever came with the prop
  const tutor = {
    ...initialTutor,
    averageRating: ratingData?.averageRating ?? initialTutor.averageRating ?? 0,
    reviewCount: ratingData?.reviewCount ?? initialTutor.reviewCount ?? 0,
  };

  const isAcademy = tutor.role === "institution";

  const selectedCategories = tutor?.selectedCategories || [];
  const categoryDetails = tutor?.categoryDetails || {};
  const teachingModes = tutor?.teachingModes || [];

  // Cap displayed names at 2 words — guards against dirty data (e.g. a
  // firstName field holding a full name, or a long registered business
  // name) that would otherwise wrap the card title onto a second line.
  const truncateToWords = (str, maxWords) =>
    (str || "").trim().split(/\s+/).filter(Boolean).slice(0, maxWords).join(" ");

  const displayName = isAcademy
    ? truncateToWords(tutor.academyName, 2) ||
      truncateToWords(tutor.lastName, 2) ||
      "Training Center"
    : `${truncateToWords(tutor.firstName, 1)} ${truncateToWords(tutor.lastName, 1)}`.trim() ||
      "Tutor";

  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    if (!lat1 || !lng1 || !lat2 || !lng2) return null;
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const distance = calculateDistance(
    userLocation?.lat,
    userLocation?.lng,
    tutor?.location?.lat,
    tutor?.location?.lng,
  );

  const distanceText =
    distance && distance > 0 ? `${distance.toFixed(1)} km away` : null;

  const avatarSrc = getImageSrc(tutor?.picture);

  const allCourses = selectedCategories.flatMap(
    (group) => categoryDetails[group]?.courses || [],
  );

  const pricingSummary = (() => {
    if (selectedCategories.length === 0) return "Rate on request";
    let lowestRate = Infinity;
    let lowestPeriod = "";
    selectedCategories.forEach((group) => {
      const details = categoryDetails[group];
      if (details?.rate) {
        const rateNum = Number(details.rate);
        if (rateNum < lowestRate) {
          lowestRate = rateNum;
          lowestPeriod = details.period || "hour";
        }
      }
    });
    return lowestRate !== Infinity
      ? formatRate(lowestRate, lowestPeriod)
      : "Rate on request";
  })();

  // Harmonized to 3 modes (online/hybrid/physical) — old keys kept as a
  // display fallback for any record not yet migrated, see
  // scripts/migrateTeachingModes.js.
  const modeLabels = {
    online: "Online",
    hybrid: "Hybrid",
    physical: "Physical",
    "online-live": "Online",
    "online-self-paced": "Online",
    "in-person-private": "Physical",
    "in-person-classroom": "Physical",
    others: "Physical",
  };

  const getShortModeName = (mode) =>
    modeLabels[mode] || mode.replace(/-/g, " ");

  const displayModes = teachingModes
    .slice(0, 2)
    .map((mode) => getShortModeName(mode));

  const about =
    tutor.bio?.trim() || tutor.about?.trim() || "No description available yet.";

  const averageRating = Number(tutor.averageRating) || 0;
  const reviewCount = Number(tutor.reviewCount) || 0;
  const hasReviews = reviewCount > 0;
  const ratingText = hasReviews
    ? `${averageRating.toFixed(1)} (${reviewCount})`
    : "New on TutorsLinks";

  const joinedText = showJoinedDate ? formatJoinedDate(tutor.createdAt) : null;

  const idOrSlug = tutor.slug || tutor._id;
  const profileRoute = isAcademy ? `/academy/${idOrSlug}` : `/tutor/${idOrSlug}`;

  const compareChecked = isSelected(tutor._id);

  const handleCompareToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!compareChecked && selected.length >= maxCompare) {
      toast.error(`You can compare up to ${maxCompare} tutors at a time.`);
      return;
    }

    toggle({
      id: tutor._id,
      slug: tutor.slug,
      displayName,
      avatar: avatarSrc,
      role: tutor.role,
    });
  };

  return (
    <div className="tutor-card">
      {enableCompare && (
        <label
          className={`compare-toggle${compareChecked ? " checked" : ""}`}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={compareChecked}
            onChange={handleCompareToggle}
          />
          Compare
        </label>
      )}

      <div className="tutor-card-header">
        {/* // 2. Replace your <div className="tutor-avatar-container"> block with this: */}
        <div className="tutor-avatar-container">
          <div
            className="avatar-clickable"
            onClick={() => setLightboxOpen(true)}
            role="button"
            aria-label={`View ${displayName}'s photo`}
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && setLightboxOpen(true)}
          >
            <img
              src={avatarSrc}
              alt={displayName}
              className="tutor-avatar"
              loading={imagePriority}
              decoding={imagePriority === "eager" ? "sync" : "async"}
              width={56}
              height={56}
              onError={(e) => {
                e.target.src = "/images/avatar3.png";
              }}
            />
            <span className="avatar-zoom-hint" aria-hidden="true">
              🔍
            </span>
          </div>

          {teachingModes.length > 0 && (
            <span className="teaching-mode-badge"> 
              {teachingModes.length === 1
                ? getShortModeName(teachingModes[0])
                : `${getShortModeName(teachingModes[0])} +${teachingModes.length - 1}`}
            </span>
          )}
        </div>

        <div className="tutor-stats">
          <div className="rating-display">
            {hasReviews && (
              <span className="stars">
                {"★".repeat(Math.floor(averageRating))}
                {"☆".repeat(5 - Math.floor(averageRating))}
              </span>
            )}
            <span className={`rating-text${hasReviews ? "" : " rating-text-new"}`}>
              {ratingText}
            </span>
          </div>

          {distanceText && (
            <div className="distance-text">📍 {distanceText}</div>
          )}
          {distance && distance < 5 && (
            <span className="nearby-badge">Nearby</span>
          )}
        </div>
      </div>

      <div className="tutor-info">
        {/* <h3 className="tutor-name">{displayName}</h3> */}
        <h3 className="tutor-name">{displayName}</h3>
        <BadgeList entity={tutor} compact max={3} />
        {joinedText && <p className="tutor-joined-date">{joinedText}</p>}
        <p className="tutor-rate">{pricingSummary}</p>

        {displayModes.length > 0 && (
          <div className="teaching-modes-small">
            {displayModes.map((mode, i) => (
              <span key={i} className="mode-pill">
                {mode}
              </span>
            ))}
            {teachingModes.length > 2 && (
              <span className="mode-pill">+{teachingModes.length - 2}</span>
            )}
          </div>
        )}

        <div className="tutor-subjects">
          {allCourses.length > 0
            ? allCourses.slice(0, 3).map((course, idx) => (
                <span key={idx} className="subject-tag">
                  {course}
                </span>
              ))
            : selectedCategories.slice(0, 2).map((cat, idx) => (
                <span key={idx} className="subject-tag">
                  {cat}
                </span>
              ))}
          {allCourses.length > 3 && (
            <span className="more-subjects">+{allCourses.length - 3}</span>
          )}
        </div>

        <p className="tutor-about">
          {about.length > 95 ? about.substring(0, 95) + "..." : about}
        </p>
      </div>

      {/* Real <Link> (not onClick+navigate) so search engines can crawl
          from listings to individual profile pages */}
      <Link to={profileRoute} className="view-profile-btn">
        {isAcademy ? "View Training Center" : "View Profile"}
      </Link>

      {/* 3. Lightbox — add this just before the closing </div> of tutor-card */}
      {lightboxOpen && (
        <div
          className="avatar-lightbox-overlay"
          onClick={() => setLightboxOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label={`${displayName}'s photo`}
        >
          <button
            className="lightbox-close"
            onClick={() => setLightboxOpen(false)}
            aria-label="Close"
          >
            &times;
          </button>
          <img
            src={avatarSrc}
            alt={displayName}
            className="lightbox-image"
            onError={(e) => {
              e.target.src = "/images/avatar3.png";
            }}
          />
          <p className="lightbox-name">{displayName}</p>
        </div>
      )}
    </div>
  );
};

export default TutorCard;
